import {
  LocationIncident,
  TimePeriod,
  UnifiedAlarmGroup,
} from '@hakimo-ui/hakimo/types';
import { DataVersionToggle } from '@hakimo-ui/hakimo/feature-shared';
import { TimePeriodSelector } from '@hakimo-ui/hakimo/ui-filters';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { ColumnsMenu } from '@hakimo-ui/hakimo/ui-table';
import { useIsSupportRole } from '@hakimo-ui/hakimo/util';
import { Button, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { ProductType } from '../state';
import { unifiedColumns } from './utils';
import { useIncidentsExport } from './useIncidentsExport';

interface Props {
  allIncidents: LocationIncident[];
  allScanIncidents?: UnifiedAlarmGroup[];
  period: TimePeriod;
  isLoadingData: boolean;
  productType: ProductType;
  onChangePeriod: (
    period: TimePeriod,
    customRange?: { start: number; end: number }
  ) => void;
  onChangeProductType: (productType: ProductType) => void;
  onOpenFilterPanel: () => void;
  isDataValid: boolean;
  hasFiltersApplied?: boolean;
  shownColumns: string[];
  onChangeShownColumns: (val: string[]) => void;
}

export function LocationIncidentsHeader(props: Props) {
  const {
    allIncidents,
    allScanIncidents,
    period,
    isLoadingData,
    onChangePeriod,
    isDataValid,
    productType,
    onChangeProductType,
    onOpenFilterPanel,
    hasFiltersApplied = false,
    shownColumns,
    onChangeShownColumns,
  } = props;

  const { isExporting, handleExport } = useIncidentsExport({
    productType,
    allIncidents,
    allScanIncidents,
  });

  const isSupportRole = useIsSupportRole();
  const disableExport = !isDataValid || !isSupportRole;

  const productTypeOptions = [
    { id: 'rg', name: 'Remote Guarding' },
    { id: 'scan', name: 'Scan' },
  ];

  const selectedProductType = productTypeOptions.find(
    (option) => option.id === productType
  );

  return (
    <div className="dark:bg-dark-bg border-b bg-white p-4 dark:border-0">
      <div className="flex h-12 flex-shrink-0 items-center justify-between gap-4">
        <div className="flex items-center justify-start gap-4">
          <div className="relative z-20 w-48">
            <SelectMenu
              value={selectedProductType}
              items={productTypeOptions}
              onChange={(option) =>
                onChangeProductType(option.id as ProductType)
              }
              displayValue={(option) => option?.name ?? ''}
              id={(option) => option.id}
            />
          </div>
          <TimePeriodSelector period={period} onChange={onChangePeriod} />
          {/* v2/v3 data-source toggle; RG-only, shown to all users. */}
          {productType === 'rg' && <DataVersionToggle />}
        </div>
        <div className="flex items-center justify-end gap-4">
          {isLoadingData && <HakimoSpinner />}
          <ColumnsMenu
            options={unifiedColumns}
            shownColumns={shownColumns}
            onChangeShownColumns={onChangeShownColumns}
          />
          <Button
            variant="icon"
            onClick={handleExport}
            loading={isExporting}
            title="Download Data"
            disabled={disableExport}
          >
            <ArrowDownTrayIcon className="w-5" />
          </Button>
          <Button
            variant="icon"
            badge={hasFiltersApplied}
            onClick={onOpenFilterPanel}
            title="Filters"
          >
            <FunnelIcon className="w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LocationIncidentsHeader;
